class CartMain extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.updateButton = null;
    this.debounceTimer = null;
    this.quantityButtons = null;
    this.quantityInputs = null;

    this.form = this.querySelector("[data-cart-form]");
    this.updateButton = this.querySelector("[data-cart-update-btn]");
    this.quantityButtons = this.querySelectorAll("[data-quantity-btn]");
    this.quantityInputs = this.querySelectorAll("[data-quantity-input]");

    this.recommendationsContainer = this.querySelector("[data-recommendations]");
    this.carousel = this.querySelector("swiper-container");
  }

  eventListeners() {
    this.quantityButtons.forEach(button => {
      button.addEventListener("click", this.handleQuantityButtonClick.bind(this));
    });

    this.quantityInputs.forEach(input => {
      input.addEventListener("change", this.handleQuantityChange.bind(this));
    });
  }

  handleQuantityButtonClick(event) {
    const button = event.currentTarget;
    const action = button.dataset.action;
    const quantityControls = button.closest("[data-quantity-controls]");
    const input = quantityControls.querySelector("[data-quantity-input]");

    if (action === "decrease") {
      input.stepDown();
    } else if (action === "increase") {
      input.stepUp();
    }

    this.submitFormDebounced();
  }

  handleQuantityChange() {
    this.submitFormDebounced();
  }

  submitFormDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.submitForm();
    }, 500);
  }

  async submitForm() {
    this.classList.add("is-updating");

    const formData = new FormData(this.form);
    const updates = {};
    formData.forEach((value, key) => {
      if (key === "updates[]") {
        const id = value; // This is tricky because multiples might exist, but usually it's line item keys
      }
    });

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        body: formData
      });

      if (!response.ok) throw new Error("Cart update failed");

      await this.refreshCart();
      app.updateCartLeadTime();
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      this.classList.remove("is-updating");
    }
  }

  async refreshCart() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=cart`);
      const data = await response.json();

      if (data["cart"]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data["cart"], "text/html");
        const newCartMain = doc.querySelector(".cart-main__inner");

        if (newCartMain) {
          if (app.latestLeadTimeStr) {
            app.updateLeadTimeUI(app.latestLeadTimeStr, newCartMain);
          }
          app.localizeLeadTimeElements(newCartMain);
          this.innerHTML = newCartMain.innerHTML;
          this.rebindElements();
        }
      }
    } catch (error) {
      console.error("Error refreshing cart section:", error);
    }
  }

  rebindElements() {
    this.form = this.querySelector("[data-cart-form]");
    this.updateButton = this.querySelector("[data-cart-update-btn]");
    this.quantityButtons = this.querySelectorAll("[data-quantity-btn]");
    this.quantityInputs = this.querySelectorAll("[data-quantity-input]");
    this.recommendationsContainer = this.querySelector("[data-recommendations]");
    this.carousel = this.querySelector("swiper-container");

    this.eventListeners();
    this.loadRecommendations();
  }

  initCarousel() {
    if (!this.carousel) return;

    const prevBtn = this.recommendationsContainer?.querySelector("[data-swiper-prev]");
    const nextBtn = this.recommendationsContainer?.querySelector("[data-swiper-next]");

    Object.assign(this.carousel, {
      slidesPerView: 1.2,
      spaceBetween: 20,
      grabCursor: true,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      breakpoints: {
        480: {
          slidesPerView: 1.5
        },
        768: {
          slidesPerView: 1.65,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0
        },
        1200: {
          slidesPerView: 2.5,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0
        }
      },
      on: {
        init: () => {
          this.carousel.classList.add("is-initialized");
          // Delay to ensure swiper is fully ready
          setTimeout(() => {
            this.updateNavButtons(prevBtn, nextBtn);
          }, 50);
        },
        slideChange: () => {
          this.updateNavButtons(prevBtn, nextBtn);
        },
        reachBeginning: () => {
          if (prevBtn) prevBtn.disabled = true;
        },
        reachEnd: () => {
          if (nextBtn) nextBtn.disabled = true;
        },
        fromEdge: () => {
          this.updateNavButtons(prevBtn, nextBtn);
        },
        touchStart: () => {
          document.body.classList.add("is-dragging-carousel");
        },
        touchEnd: () => {
          document.body.classList.remove("is-dragging-carousel");
        }
      }
    });

    this.carousel.initialize();

    // Bind navigation buttons
    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        this.carousel.swiper?.slidePrev();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        this.carousel.swiper?.slideNext();
      });
    }
  }

  updateNavButtons(prevBtn, nextBtn) {
    const swiper = this.carousel?.swiper;
    if (!swiper) return;

    if (prevBtn) {
      prevBtn.disabled = swiper.isBeginning;
    }
    if (nextBtn) {
      nextBtn.disabled = swiper.isEnd;
    }
  }

  async loadRecommendations() {
    if (!this.recommendationsContainer) return;

    const productIds = this.recommendationsContainer.dataset.productIds;
    const productIdsInCart = this.recommendationsContainer.dataset.productIdsInCart.split(",").map(id => id.trim());
    const limit = parseInt(this.recommendationsContainer.dataset.limit) || 10;
    const addLabel = this.recommendationsContainer.dataset.addLabel || "Add";

    if (!productIds) return;

    const productIdArray = productIds.split(",").map(id => id.trim());
    const allRecommendations = [];
    const seenProductIds = new Set(productIdsInCart);

    // Fetch recommendations for each product in cart
    for (const productId of productIdArray) {
      try {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`);

        if (!response.ok) continue;

        const data = await response.json();

        if (data.products && data.products.length > 0) {
          for (const product of data.products) {
            // Skip if already in cart or already added to recommendations
            if (!seenProductIds.has(String(product.id))) {
              seenProductIds.add(String(product.id));
              allRecommendations.push(product);
            }
          }
        }
      } catch (error) {
        console.error(`Error fetching recommendations for product ${productId}:`, error);
      }

      // Stop if we have enough recommendations
      if (allRecommendations.length >= limit) break;
    }

    // Limit to max number
    const recommendations = allRecommendations.slice(0, limit);

    if (recommendations.length === 0) {
      this.recommendationsContainer.style.display = "none";
      return;
    }

    // Render recommendations
    this.renderRecommendations(recommendations, addLabel);
  }

  renderRecommendations(products, addLabel) {
    const swiperContainer = this.recommendationsContainer.querySelector("swiper-container");
    if (!swiperContainer) return;

    const slidesHTML = products
      .map(product => {
        const variantId = product.variants[0]?.id;
        const price = this.formatMoney(product.price);
        const imageUrl = product.featured_image ? product.featured_image.replace(/\.([^.]+)$/, "_200x.$1") : "";
        const fabricPlaceholder = this.recommendationsContainer.dataset.fabricPlaceholder;
        const fabricTitle = product.variants[0].option1 || null;
        const fabricText = fabricTitle && (product.type == "Furniture" || product.type == "Furniture - hard") ? fabricPlaceholder.replace("#fabric#", fabricTitle) : null;
        return `
        <swiper-slide>
          <div class="cart-main__recommendations-item">
            <a class="cart-main__recommendations-item-image-link" href="${product.url}">
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" width="100" height="auto">` : ""}
            </a>
            <div class="cart-main__recommendations-item-content">
              <a class="t-bold" href="${product.url}">${product.title}</a>
              ${
                fabricText
                  ? `
              <span class="cart-main__recommendations-item-fabric t-xsmall c-gray">${fabricText}</span>
              `
                  : ""
              }
              <form action="/cart/add" method="post">
                <input type="hidden" name="id" value="${variantId}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="btn btn--link btn--link-small">
                  <span>+ ${addLabel}</span>
                </button>
              </form>
            </div>
            <div class="cart-main__recommendations-item-price">
              <p class="t-small t-bold">${price}</p>
            </div>
          </div>
        </swiper-slide>
      `;
      })
      .join("");

    swiperContainer.innerHTML = slidesHTML;

    // Show the container and initialize carousel
    this.recommendationsContainer.style.display = "";
    this.carousel = swiperContainer;
    this.initCarousel();
  }

  formatMoney(cents) {
    const format = window.Shopify?.moneyFormat || "{{amount}} PLN";

    function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);
      const parts = number.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousands);
      const cents = parts[1] ? decimal + parts[1] : "";

      return dollars + cents;
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return cents;

    let value = "";
    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  init() {
    this.eventListeners();
    this.loadRecommendations();
  }

  connectedCallback() {
    this.init();
  }
}

if (!customElements.get("cart-main")) customElements.define("cart-main", CartMain);
